import { SurfaceCategorySlug, SurfaceTypeSlug } from "@solomon-apps/shared";
import type { ClientPositionView, ClientProgramSurfaceOption, ProgramCenteredListResponse } from "@solomon-apps/shared";

import type {
    ClientAssignedProgramView,
    ClientEffectivePolicy,
    ClientProgram,
    ClientProgramPosition,
    ClientProgramSurfaceView,
    CreateClientProgramPositionInput,
    CreateClientRunProgramInput
} from "@/types/programCenteredYaas";

import { orgApiFetch } from "./orgApiFetch";

function appendQuery(path: string, params?: Record<string, string | number | undefined>): string {
    const query = new URLSearchParams();
    Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value !== undefined) query.set(key, String(value));
    });
    const qs = query.toString();
    return qs ? `${path}?${qs}` : path;
}

export function fetchClientPrograms(params?: {
    limit?: number;
    page?: number;
    offset?: number;
}): Promise<ProgramCenteredListResponse<ClientAssignedProgramView>> {
    return orgApiFetch<ProgramCenteredListResponse<ClientAssignedProgramView>>(
        appendQuery("/api/org/client/programs", params)
    );
}

export function createClientRunProgram(input: CreateClientRunProgramInput): Promise<ClientProgram> {
    return orgApiFetch<ClientProgram>("/api/org/client/programs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input)
    });
}

export function fetchClientEffectivePolicies(
    programUuid: string
): Promise<ProgramCenteredListResponse<ClientEffectivePolicy>> {
    return orgApiFetch<ProgramCenteredListResponse<ClientEffectivePolicy>>(
        `/api/org/client/programs/${encodeURIComponent(programUuid)}/effective-policies`
    );
}

export interface PositionVerificationChallenge {
    requiresRetailAccountClaim?: boolean;
    nonce: string;
    message: string;
    expiresAt: string;
    microTransferMemo: string;
}

export function issuePositionVerificationChallenge(
    input: Pick<
        CreateClientProgramPositionInput,
        "programSurfaceUuid" | "walletAddress" | "sourceKind" | "signerAddress" | "multisigProgramId"
    >
): Promise<PositionVerificationChallenge> {
    return orgApiFetch<PositionVerificationChallenge>("/api/org/client/positions/challenge", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input)
    });
}

export function createClientProgramPosition(input: CreateClientProgramPositionInput): Promise<ClientProgramPosition> {
    return orgApiFetch<ClientProgramPosition>("/api/org/client/positions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input)
    });
}

function firstObservationValue(view: ClientProgramSurfaceView): string {
    const observationSources = view.surface.observationSources ?? {};
    const preferredKeys = ["poolAddress", "pool", "address", "vaultAddress"];
    for (const key of preferredKeys) {
        const value = observationSources[key];
        if (typeof value === "string" && value.trim()) return value;
    }
    const first = Object.values(observationSources).find(
        (value): value is string => typeof value === "string" && value.trim().length > 0
    );
    return first ?? view.surface.asset?.mintAddress ?? view.surface.uuid;
}

function programSurfaceTypeFor(view: ClientProgramSurfaceView): string {
    // The reserved category slug is the contract; the free-text scan below it
    // catches provider categories the catalog adds without a reserved slug.
    if (view.surfaceCategory.slug === SurfaceCategorySlug.Address) return "ADDRESS";

    const source = `${view.surfaceCategory.slug} ${view.surfaceType.slug} ${view.surfaceType.name}`.toLowerCase();
    if (source.includes("neobank")) return "NEOBANK";
    if (source.includes("treasury")) return "TREASURY";
    if (source.includes("raydium") || source.includes("lp") || source.includes("pool") || source.includes("liquidity"))
        return "DEX_LP";

    // Was `participationMode === "ClientEndUserEnrollments" ? "PROGRAM" : "TREASURY"`.
    // yaas-api retired the participation classification, so that read was always
    // undefined and every unmatched Surface already landed on TREASURY. Kept as
    // the fallback, now stated rather than reached through a dead comparison.
    return "TREASURY";
}

function surfaceLabelFor(view: ClientProgramSurfaceView): string {
    return view.surfaceType.name.trim() || view.surfaceCategory.name.trim() || view.surface.name;
}

function productLabelFor(view: ClientProgramSurfaceView): string {
    const name = view.surface.name.trim();
    const surface = surfaceLabelFor(view);
    const isAccountSurface =
        view.surfaceCategory.slug === SurfaceCategorySlug.Address ||
        view.surfaceType.slug === SurfaceTypeSlug.WalletAccount;

    if (isAccountSurface && name.toLowerCase().startsWith(surface.toLowerCase())) {
        return name.slice(surface.length).trim() || name;
    }

    return name;
}

export function clientProgramSurfaceToOption(view: ClientProgramSurfaceView): ClientProgramSurfaceOption {
    return {
        uuid: view.programSurface.uuid,
        name: view.surface.name,
        displayName: view.surface.name,
        description: view.surface.description,
        product: productLabelFor(view),
        surface: surfaceLabelFor(view),
        type: programSurfaceTypeFor(view),
        address: firstObservationValue(view),
        trailingApyPct: null,
        tvl: "0",
        yaasProtocol: {
            uuid: view.surfaceType.uuid,
            name: view.surfaceType.providerName ?? view.surfaceType.name,
            slug: view.surfaceType.slug
        }
    };
}

export function fallbackPositionViewFromProgramPosition(args: {
    position: ClientProgramPosition;
    input: CreateClientProgramPositionInput;
    programSurface?: ClientProgramSurfaceOption | null;
}): ClientPositionView {
    const now = new Date().toISOString();
    const programSurface = args.programSurface ?? {
        uuid: args.input.programSurfaceUuid,
        name: "Program Surface",
        displayName: "Program Surface",
        type: "PROGRAM",
        address: args.input.programSurfaceUuid,
        trailingApyPct: null,
        tvl: "0",
        yaasProtocol: {
            uuid: "program-surface",
            name: "Program",
            slug: "program"
        }
    };

    return {
        uuid: args.position.uuid,
        status: args.position.status as ClientPositionView["status"],
        manualUsdvAmount: args.position.manualUsdvAmount,
        totalRewards: args.position.totalRewards,
        rewardsDestinationAddress: args.input.rewardsDestinationAddress ?? null,
        rejectionReason: null,
        rejectedAt: null,
        suspensionReason: null,
        suspendedAt: null,
        disabledAt: null,
        hiddenAt: null,
        verifiedAt: null,
        verificationMode: args.input.verificationMode,
        manualReviewDescription: args.input.manualReviewDescription ?? null,
        sourceKind: args.input.sourceKind,
        multisigProgramId: args.input.multisigProgramId ?? null,
        programSurface: {
            uuid: programSurface.uuid,
            name: programSurface.name,
            product: programSurface.product ?? programSurface.name,
            surface: programSurface.surface ?? null,
            type: programSurface.type,
            address: programSurface.address,
            trailingApyPct: programSurface.trailingApyPct
        },
        account: {
            uuid: args.position.participantAccountUuid,
            label: args.input.label ?? "Program wallet",
            address: args.input.walletAddress
        },
        createdAt: args.position.createdAt ?? now,
        updatedAt: args.position.updatedAt ?? now
    };
}
